/**
 * Fire bubbles (spec 02 §8 Fire), ported from rebb64: a burst fire bubble becomes a drop in its slot
 * (routine_3DB0 $3E5E: type $24), which falls 4 px a pass (L11B7, run from the entity pass D_0CF2) and,
 * on the first platform top, lights one of five burning lines (L11F2). Each pass D_1319 (misnamed
 * update_player_sprites) draws every line, burns the monsters standing on it while its flames are
 * tall, and puts it out.
 */

import { rngOf } from '../random';
import { DROP, GONE, QUIET_POP } from './codes';
import { killBy } from './items';
import { trappable } from './shot';
import { colOfX, drawnY, endInSlot, freeBubble, mapSolid } from './slots';
import { systemOf } from './system';
import {
  DROP_END_ROW, DROP_STEP, FIRE_CHARS, FIRE_DEADLY, FIRE_KILL_DY, FIRE_LINES, FIRE_REACH_MASK,
  FIRE_RIGHT_LIMIT, FIRE_TIMER,
} from './tuning';
import type { Bubble, PlayerId, World } from '../types';
import type { FireDrop, FireLine } from './state';

/** $3E5E: the bubble becomes a drop in its slot, keeping its X and row, its sub-row cleared (D_A9D6 = 0). */
export function startDrop(world: World, b: Bubble, credit: PlayerId | -1): void {
  freeBubble(world, b);
  systemOf(world).drops.push({ slot: b.motion.slot, x: b.x, row: b.motion.row, subY: 0, credit });
}

/**
 * L11B7, one pass of a drop (none while the clock's freeze is on: L0D4E skips types $24-$33 while $67
 * counts). The sub-row toggles; on the 4 the row steps down (to row $1D: gone). On the 0 it lands if the
 * cell of its top tile (row - 3) is solid and the one above it (row - 4) is not - so it sinks through
 * the platform it starts in and stops with its top in the first platform below.
 */
export function dropStep(world: World, d: FireDrop): void {
  if (world.power.clock > 0) return;
  d.subY ^= DROP_STEP;
  if (d.subY !== 0) {
    d.row++;
    if (d.row >= DROP_END_ROW) {
      d.row--;
      endDrop(world, d, GONE);
    }
    return;
  }
  const col = colOfX(d.x);
  if (mapSolid(world, col, d.row - 4) || !mapSolid(world, col, d.row - 3)) return;
  endDrop(world, d, GONE);
  light(world, d, col);
}

/** The drop leaves its list for its slot's vanish ($38) or, at the level-clear sweep, a burst ($3A). */
function endDrop(world: World, d: FireDrop, code: number): void {
  const sys = systemOf(world);
  sys.drops.splice(sys.drops.indexOf(d), 1);
  endInSlot(world, d.slot, { x: d.x, y: drawnY(d.row, d.subY), row: d.row, subY: d.subY }, code, DROP);
}

/** $171B: the level-clear sweep turns every drop into a burst without points. */
export function burstDrops(world: World): void {
  for (const d of systemOf(world).drops.slice()) endDrop(world, d, QUIET_POP);
}

/**
 * L11E7..L125B: the first free line from the last (none: no fire). Reach rand & 15 left, then rand & 15
 * right (dropped whole if it takes the column to $1E). A cell burns while the tile above the platform
 * is open and the platform tile under it solid. The left scan runs from the drop's column - 1, each
 * test counting a cell, the failing one too; the right scan tests from column + 2, counting only the
 * cells that pass - the C64's own arithmetic, so a line's right end sits a cell or two short of what
 * was tested.
 */
function light(world: World, d: FireDrop, col: number): void {
  const sys = systemOf(world);
  let slot = -1;
  for (let s = FIRE_LINES - 1; s >= 0 && slot < 0; s--) if ((sys.fires[s]?.timer ?? 0) < 0) slot = s;
  if (slot < 0) return;
  const rng = rngOf(world);
  let left = rng.int(256) & FIRE_REACH_MASK;
  let right = rng.int(256) & FIRE_REACH_MASK;
  if (right + col >= FIRE_RIGHT_LIMIT) right = -1;
  const burns = (c: number): boolean => !mapSolid(world, c, d.row - 4) && mapSolid(world, c, d.row - 3);
  let len = 0;
  let from = col;
  for (let c = col - 1; ; c--) {
    len++;
    if (!burns(c)) break;
    from = c;
    if (--left < 0) break;
  }
  if (right >= 0) {
    for (let c = col + 2; burns(c); c++) {
      len++;
      if (--right < 0) break;
    }
  }
  sys.fires[slot] = { col: from, row: d.row, len, timer: FIRE_TIMER, credit: d.credit };
  world.events.push({ type: 'burn' });
}

/**
 * D_1319, once a pass before the bubbles, lines 4..0: the timer steps (see FIRE_CHARS), the character
 * of its old value is drawn, and a tall flame ($40, $41) burns: every monster (states 1..$0A, from the
 * last) whose Y is exactly 16 above the line's, standing on its platform, with 0 <= X - the line's X
 * < 8 x its length (8-bit) - green diamond. Never players. Monsters frozen by the heart are in states
 * $16 and up, which the test skips.
 */
export function burnFires(world: World): void {
  const sys = systemOf(world);
  for (let s = FIRE_LINES - 1; s >= 0; s--) {
    const f = sys.fires[s];
    if (!f || f.timer < 0) continue;
    const ch = FIRE_CHARS[f.timer] ?? 0;
    f.timer--;
    if (ch >= FIRE_DEADLY) burn(world, f);
  }
}

function burn(world: World, f: FireLine): void {
  if (world.power.heart !== -1) return;
  const fx = (f.col * 8 + 0x14) & 0xff;
  const fy = (f.row * 8 + 0x15) & 0xff;
  const width = (f.len * 8) & 0xff;
  for (let i = world.monsters.length - 1; i >= 0; i--) {
    const mon = world.monsters[i];
    if (!mon || !trappable(mon)) continue;
    if (((fy - mon.y) & 0xff) !== FIRE_KILL_DY || ((mon.x - fx) & 0xff) >= width) continue;
    killBy(world, mon.id, 'fire');
  }
}

/** What the renderer draws of fire (see `fireLook` in index.ts). */
export function fireView(world: World): {
  drops: { x: number; y: number }[]; cells: { col: number; row: number; deadly: boolean; frame: 0 | 1 | 2 }[];
} {
  const sys = systemOf(world);
  const drops = sys.drops.map((d) => ({ x: d.x & 0xff, y: drawnY(d.row, d.subY) }));
  const cells: { col: number; row: number; deadly: boolean; frame: 0 | 1 | 2 }[] = [];
  for (const f of sys.fires) {
    // The character drawn this pass is that of the timer before its step; 6 is lit, not drawn yet.
    const ch = f.timer >= 0 && f.timer < FIRE_TIMER ? FIRE_CHARS[f.timer + 1] ?? 0 : 0;
    const row = f.row - 4;
    if (ch === 0 || row < 0 || row > 24) continue;
    const frame = ch < FIRE_DEADLY ? 0 : ch === FIRE_DEADLY ? 1 : 2;
    for (let c = f.col; c < f.col + f.len; c++) cells.push({ col: c, row, deadly: ch >= FIRE_DEADLY, frame });
  }
  return { drops, cells };
}
